/**
 * ASCII Art Generator - 图片导出器
 *
 * 支持PNG、JPG、WebP格式的图片导出
 */

import type { ImageExportConfig } from './types'
import { DEFAULT_IMAGE_EXPORT_CONFIG } from './types'

// ============================================
// 图片导出器类
// ============================================

/**
 * 图片导出器
 */
export class ImageExporter {
  /**
   * 导出Canvas为图片Blob
   */
  async export(
    canvas: HTMLCanvasElement,
    config: Partial<ImageExportConfig> = {}
  ): Promise<Blob> {
    const finalConfig = { ...DEFAULT_IMAGE_EXPORT_CONFIG, ...config }

    // 1. 处理缩放
    const scaledCanvas = this.scaleCanvas(canvas, finalConfig.scale)

    // 2. 处理透明背景（JPG不支持透明）
    if (finalConfig.format === 'jpg') {
      const filledCanvas = this.fillBackground(scaledCanvas, finalConfig.backgroundColor || '#000000')
      return this.canvasToBlob(filledCanvas, finalConfig)
    }

    // 3. 转换为Blob
    return this.canvasToBlob(scaledCanvas, finalConfig)
  }

  /**
   * Canvas转Blob
   */
  private canvasToBlob(canvas: HTMLCanvasElement, config: ImageExportConfig): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const mimeType = `image/${config.format}`
      const quality = config.format === 'png' ? undefined : config.quality / 100

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Canvas toBlob 返回空结果'))
          }
        },
        mimeType,
        quality
      )
    })
  }

  /**
   * 缩放Canvas
   */
  private scaleCanvas(source: HTMLCanvasElement, scale: number): HTMLCanvasElement {
    if (scale === 1) return source

    const scaled = document.createElement('canvas')
    scaled.width = source.width * scale
    scaled.height = source.height * scale

    const ctx = scaled.getContext('2d')!
    ctx.imageSmoothingEnabled = scale > 1
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(source, 0, 0, scaled.width, scaled.height)

    return scaled
  }

  /**
   * 检查Canvas是否有透明像素
   */
  private hasTransparency(canvas: HTMLCanvasElement): boolean {
    const ctx = canvas.getContext('2d')!
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const data = imageData.data

    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) return true
    }
    return false
  }

  /**
   * 填充背景色（用于JPG导出）
   */
  private fillBackground(canvas: HTMLCanvasElement, color: string): HTMLCanvasElement {
    if (!this.hasTransparency(canvas)) {
      return canvas
    }

    const filledCanvas = document.createElement('canvas')
    filledCanvas.width = canvas.width
    filledCanvas.height = canvas.height

    const filledCtx = filledCanvas.getContext('2d')!
    filledCtx.fillStyle = color
    filledCtx.fillRect(0, 0, filledCanvas.width, filledCanvas.height)
    filledCtx.drawImage(canvas, 0, 0)

    return filledCanvas
  }

  /**
   * 获取Canvas的DataURL
   */
  getDataURL(
    canvas: HTMLCanvasElement,
    config: Partial<ImageExportConfig> = {}
  ): string {
    const finalConfig = { ...DEFAULT_IMAGE_EXPORT_CONFIG, ...config }
    const mimeType = `image/${finalConfig.format}`
    const quality = finalConfig.format === 'png' ? undefined : finalConfig.quality / 100

    return canvas.toDataURL(mimeType, quality)
  }
}

// ============================================
// 工厂函数
// ============================================

export function createImageExporter(): ImageExporter {
  return new ImageExporter()
}